import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { jwtDecode } from 'jwt-decode'
import { scannerApi } from 'shared/api/scanner'
import { clearSession, getStoredUser, getToken, setStoredUser, setTokens } from 'shared/util/storage'

function mergeUser(profile, token) {
  let decoded = {}
  try {
    decoded = token ? jwtDecode(token) : {}
  } catch {
    decoded = {}
  }
  return {
    ...decoded,
    ...(profile || {}),
    aEventPermissions: decoded.aEventPermissions || profile?.aEventPermissions,
    eType: decoded.eType || profile?.eType,
    eUserType: decoded.eUserType || profile?.eUserType
  }
}

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  login: () => {},
  logout: async () => {}
})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => mergeUser(getStoredUser(), getToken()))

  const login = useCallback((payload) => {
    const token = payload?.sToken
    setTokens(token, payload?.sRefreshToken)
    setStoredUser(payload)
    setUser(mergeUser(payload, token))
  }, [])

  const logout = useCallback(async () => {
    try {
      if (getToken()) await scannerApi.logout()
    } catch {
      // still clear locally
    } finally {
      clearSession()
      setUser(null)
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(getToken()),
      login,
      logout
    }),
    [user, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node
}

export function useAuth() {
  return useContext(AuthContext)
}
